/**
 * Time Utilities
 * Functions for time formatting and calculations
 */

const MILLISECONDS_PER_MINUTE = 60 * 1000;
const MILLISECONDS_PER_HOUR = 60 * MILLISECONDS_PER_MINUTE;

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const MERIDIEM_PM = 'pm';
const MERIDIEM_AM = 'am';
const DATE_PART_TODAY = 'today';
const DATE_PART_YESTERDAY = 'yesterday';
const DAY_DIVIDER_TODAY = 'Today';
const DAY_DIVIDER_YESTERDAY = 'Yesterday';

enum RelativeTimeUnit {
  Day = 'day',
  Hour = 'hour',
  Minute = 'minute',
}

function formatTime(date: Date): string {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const isPM = hours >= 12;

  if (hours > 12) {
    hours -= 12;
  } else if (hours === 0) {
    hours = 12;
  }

  return `${hours}:${String(minutes).padStart(2, '0')}${isPM ? MERIDIEM_PM : MERIDIEM_AM}`;
}

function isSameCalendarDay(first: Date, second: Date): boolean {
  return first.getFullYear() === second.getFullYear()
    && first.getMonth() === second.getMonth()
    && first.getDate() === second.getDate();
}

function isYesterday(date: Date, now: Date): boolean {
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  return isSameCalendarDay(date, yesterday);
}

function formatDatePart(date: Date, now: Date): string {
  if (isSameCalendarDay(date, now)) {
    return DATE_PART_TODAY;
  }

  if (isYesterday(date, now)) {
    return DATE_PART_YESTERDAY;
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (date.getFullYear() === now.getFullYear()) {
    return `${month}.${day}`;
  }

  return `${month}.${day}.${date.getFullYear()}`;
}

/**
 * Converts a Date object to a human-readable "time ago" format
 * @param date - The date to format
 * @returns Formatted string like "2h ago", "5d ago", etc.
 */
export function getTimeAgo(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

/**
 * Formats an ISO timestamp to chat display format
 * @param isoString - ISO 8601 date string
 * @returns Formatted string with relative and calendar-aware rules
 */
export function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs >= 0 && diffMs < 2 * MILLISECONDS_PER_MINUTE) {
    return 'just now';
  }

  if (diffMs >= 0 && diffMs < MILLISECONDS_PER_HOUR) {
    const minutesAgo = Math.floor(diffMs / MILLISECONDS_PER_MINUTE);
    return `${minutesAgo} minutes ago`;
  }

  return `${formatTime(date)} ${formatDatePart(date, now)}`;
}

/**
 * Formats a date for day dividers
 * @param date - Date to format
 * @returns "Today", "Yesterday", or "Month DD, YYYY"
 */
export function formatDayDivider(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  const diffDays = Math.floor((today.getTime() - compareDate.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return DAY_DIVIDER_TODAY;
  if (diffDays === 1) return DAY_DIVIDER_YESTERDAY;
  
  // Format as "Month DD, YYYY"
  return `${MONTHS_FULL[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * Formats a Date to a readable label like "Apr 6, 2026"
 */
export function formatReadableDate(date: Date): string {
  return `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}, ${formatTime(date)}`;
}

/**
 * Formats a Date to a short calendar label like "Apr 6, 2026" (no time).
 */
export function formatShortDate(date: Date): string {
  return `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * Returns a verbose, bidirectional relative time string.
 * Past: "3 days ago", "5 hours ago", "12 minutes ago"
 * Future: "in 3 days", "in 5 hours", "in 12 minutes"
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const absMs = Math.abs(diffMs);
  const isPast = diffMs < 0;

  if (absMs < 2 * MILLISECONDS_PER_MINUTE) {
    return 'just now';
  }

  const days  = Math.floor(absMs / (MILLISECONDS_PER_HOUR * 24));
  const hours = Math.floor(absMs / MILLISECONDS_PER_HOUR);
  const mins  = Math.floor(absMs / MILLISECONDS_PER_MINUTE);

  const fmt = (n: number, unit: string) =>
    isPast
      ? `${n} ${unit}${n !== 1 ? 's' : ''} ago`
      : `in ${n} ${unit}${n !== 1 ? 's' : ''}`;

  if (days  >= 1) return fmt(days,  RelativeTimeUnit.Day);
  if (hours >= 1) return fmt(hours, RelativeTimeUnit.Hour);
  return fmt(mins, RelativeTimeUnit.Minute);
}

/**
 * Gets the day key for grouping messages
 * @param date - Date to get key for
 * @returns Date string in YYYY-MM-DD format
 */
export function getDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
